/**
 * CreateTransferView — Full-page view for creating a new HTE warehouse transfer.
 * Reuses the ICS detail-view layout: header form grid, items table with inline editing,
 * weight management side panel, and total weight box.
 * Saves header + all items + all weights in one flow before navigating back.
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../../../hooks/useAuth';
import { isElevatedRole } from '../../../constants/queryConfig';
import { useLanguage } from '../../../i18n/LanguageContext';
import { inventoryService } from '../../../services/inventoryService';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Save,
  Send,
  Search,
  Trash2,
  Package,
  ChevronDown,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import DatePickerDropdown from '../../../components/DatePickerDropdown';
import {
  hteTransferService,
  CreateTransferRequest,
  InventorySearchItem,
} from '../../../services/hteTransferService';
import ItemSearchModal from './ItemSearchModal';
import TransferDetailForm, { LocalItem } from './TransferDetailForm';
import { getDateCode, fromDateCode } from '../../../utils/dateUtils';

// Convert YYYYMMDD to YYYY-MM-DD for HTML date inputs
const parseDbDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  // If already in YYYY-MM-DD format, return as-is
  if (dateStr.includes('-')) return dateStr.slice(0, 10);
  // Convert YYYYMMDD to YYYY-MM-DD
  if (dateStr.length === 8) {
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
  }
  return '';
};

interface CreateTransferViewProps {
  onBack: () => void;
  onCreated: (transferNumber: string) => void;
  editTransferNumber?: string | null;
}

/** Temp ID counter for local items before they are saved to the server */
let _tempId = 0;
const nextTempId = () => --_tempId;

// LocalItem type is imported from TransferDetailForm

const CreateTransferView = ({ onBack, onCreated, editTransferNumber }: CreateTransferViewProps) => {
  const { user } = useAuth();
  const { t } = useLanguage();

  // ---- Header State ----
  const [requestDate, setRequestDate] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [warehouseFrom, setWarehouseFrom] = useState('WF');
  const [locationFrom, setLocationFrom] = useState('');
  const [warehouseTo, setWarehouseTo] = useState('HTE1');
  const [locationTo, setLocationTo] = useState('');
  const [memo, setMemo] = useState('');
  const [requestBy, setRequestBy] = useState('');
  const [receiveBy, setReceiveBy] = useState('');

  // ---- Items State ----
  const [localItems, setLocalItems] = useState<LocalItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [newWeightInput, setNewWeightInput] = useState<Record<number, string>>({});

  // ---- Pending (staged) item — selected from modal but not yet added to table ----
  const [pendingItem, setPendingItem] = useState<LocalItem | null>(null);

  // ---- Item Search State ----
  const [itemQuery, setItemQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  // ---- Saving State ----
  const [saving, setSaving] = useState(false);

  // Track whether the draft was auto-generated (from session materials)
  const draftSourceRef = useRef('');

  // ---- Dropdown Data ----
  const { data: warehouseCodes = [] } = useQuery(
    ['ics-warehouse-codes'],
    () => inventoryService.getWarehouseCodes()
  );
  const { data: locationCodes = [] } = useQuery(
    ['ics-location-codes'],
    () => inventoryService.getLocationCodes()
  );

  // ---- Load existing draft when editing ----
  const [loadingDraft, setLoadingDraft] = useState(!!editTransferNumber);

  useEffect(() => {
    if (!editTransferNumber) return;
    let cancelled = false;
    (async () => {
      try {
        const t = await hteTransferService.getTransfer(editTransferNumber);
        if (cancelled) return;
        draftSourceRef.current = t.source || '';
        setRequestDate(parseDbDate(t.request_date));
        setTransferDate(parseDbDate(t.transfer_date));
        setWarehouseFrom(t.warehouse_code_from || '');
        setLocationFrom(t.location_code_from || '');
        setWarehouseTo(t.warehouse_code_to || '');
        setLocationTo(t.location_code_to || '');
        setMemo(t.memo || '');
        setRequestBy(t.request_by || '');
        setReceiveBy(t.receive_by || '');
        // Load line items
        if (t.details && t.details.length > 0) {
          const items: LocalItem[] = t.details.map(d => ({
            tempId: nextTempId(),
            serverId: d.id,
            item_number: d.item_number,
            item_english_name: d.item_english_name,
            item_chinese_name: d.item_chinese_name,
            item_unit: d.item_unit,
            unit_weight: d.unit_weight,
            quantity_request: parseFloat(String(d.quantity_request)) || 0,
            quantity_shipped: parseFloat(String(d.quantity_shipped)) || 0,
            weight_shipped: parseFloat(String(d.weight_shipped)) || 0,
            is_frozen: d.is_frozen || 'N',
            date_code: d.date_code || '',
            date_code_date: d.date_code
              ? (d.date_code.length === 8
                ? `${d.date_code.slice(0,4)}-${d.date_code.slice(4,6)}-${d.date_code.slice(6,8)}`
                : fromDateCode(d.date_code))
              : '',
            remark: d.remark || '',
            weights: (d.weights || []).map(w => ({ tempId: nextTempId(), weight: parseFloat(String(w.weight)) || 0 })),
          }));
          setLocalItems(items);
        }
      } catch (err) {
        console.error('Failed to load draft transfer', err);
        toast.error('Failed to load draft transfer');
      } finally {
        if (!cancelled) setLoadingDraft(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editTransferNumber]);

  // ---- Stage item from search modal (populates detail form, not yet in table) ----
  const handleSelectItem = (item: InventorySearchItem) => {
    const tempId = nextTempId();
    const dateCode = getDateCode(requestDate);
    const staged: LocalItem = {
      tempId,
      item_number: item.item_number,
      item_english_name: item.item_english_name,
      item_chinese_name: item.item_chinese_name,
      item_unit: item.item_unit,
      unit_weight: item.unit_weight,
      quantity_request: 0,
      quantity_shipped: 0,
      weight_shipped: 0,
      is_frozen: 'N',
      date_code: dateCode,
      date_code_date: requestDate || '',
      remark: '',
      weights: [],
      special_request: '',
      avg_sold: '',
      avg_back: '',
      qs_type: '',
      duration: '',
      scan_complete: '',
      qs_pend: '',
      qs_rec_number: '',
      salesperson_qs1: '',
      q_item: '',
    };
    setPendingItem(staged);
    setSelectedItemId(null); // deselect any table row
    setItemQuery('');
  };

  // ---- Commit pending item into the line items table ----
  const handleCommitPendingItem = () => {
    if (!pendingItem) return;
    setLocalItems(prev => [...prev, pendingItem]);
    setSelectedItemId(pendingItem.tempId);
    setPendingItem(null);
  };

  /** Update a field on the pending (staged) item */
  const updatePendingField = (field: keyof LocalItem, value: any) => {
    if (!pendingItem) return;
    setPendingItem(prev => prev ? { ...prev, [field]: value } : null);
  };

  // ---- Remove Item ----
  const handleRemoveItem = (tempId: number) => {
    setLocalItems(prev => prev.filter(i => i.tempId !== tempId));
    if (expandedRowId === tempId) setExpandedRowId(null);
    if (selectedItemId === tempId) setSelectedItemId(null);
  };

  // ---- Weight Management ----
  const handleAddWeight = (tempId: number) => {
    const val = parseFloat(newWeightInput[tempId] || '0');
    if (!val || val <= 0) {
      toast.error(t('warehouseTransfer.weightPositive'));
      return;
    }
    setLocalItems(prev =>
      prev.map(item =>
        item.tempId === tempId
          ? { ...item, weights: [...item.weights, { tempId: nextTempId(), weight: val }] }
          : item
      )
    );
    setNewWeightInput(prev => ({ ...prev, [tempId]: '' }));
  };

  const handleRemoveWeight = (itemTempId: number, weightTempId: number) => {
    setLocalItems(prev =>
      prev.map(item =>
        item.tempId === itemTempId
          ? { ...item, weights: item.weights.filter(w => w.tempId !== weightTempId) }
          : item
      )
    );
  };

  // Effective weight: sum of piece weights, or weight_shipped fallback
  const getEffectiveWeight = (item: LocalItem): number => {
    if (item.weights.length > 0) {
      return item.weights.reduce((sum, w) => sum + w.weight, 0);
    }
    return item.weight_shipped || 0;
  };

  const totalWeight = useMemo(() => {
    return localItems.reduce((sum, item) => sum + getEffectiveWeight(item), 0);
  }, [localItems]);

  const expandedItem = localItems.find(i => i.tempId === expandedRowId) || null;
  const selectedItem = localItems.find(i => i.tempId === selectedItemId) || null;

  /** Update a single field on the selected item directly in localItems state */
  const updateSelectedField = (field: keyof LocalItem, value: any) => {
    if (!selectedItemId) return;
    setLocalItems(prev =>
      prev.map(item =>
        item.tempId === selectedItemId ? { ...item, [field]: value } : item
      )
    );
  };

  /** Handle quantity_shipped change. */
  const handleQuantityShippedChange = (qty: number) => {
    const formItem = pendingItem || selectedItem;
    const updateField = pendingItem ? updatePendingField : updateSelectedField;
    if (!formItem) return;
    updateField('quantity_shipped', qty);
    // WCO-2507: do NOT auto-generate weight entries or weight_shipped from qty × unit_weight.
    // The VB6 ship-out scan captures the real per-box weights; the webapp must not fabricate an
    // estimated ship weight. weight_shipped stays 0 until real weights are entered/scanned.
  };

  // ---- Save Flow ----
  const handleSave = async (andSubmit: boolean) => {
    // Validate: need either warehouse or location for each side
    if (!warehouseFrom && !locationFrom) {
      toast.error(t('warehouseTransfer.warehouseFromRequired'));
      return;
    }
    if (!warehouseTo && !locationTo) {
      toast.error(t('warehouseTransfer.warehouseToRequired'));
      return;
    }

    setSaving(true);
    try {
      const headerData: CreateTransferRequest = {
        request_date: requestDate || undefined,
        transfer_date: transferDate || undefined,
        warehouse_code_from: warehouseFrom,
        location_code_from: locationFrom || undefined,
        warehouse_code_to: warehouseTo,
        location_code_to: locationTo || undefined,
        memo: memo || undefined,
        request_by: requestBy || undefined,
        receive_by: receiveBy || undefined,
        created_by: user?.username || undefined,
      };

      let transferNumber: string;

      if (editTransferNumber) {
        // ---- Edit mode: update existing draft ----
        await hteTransferService.updateTransfer(editTransferNumber, headerData);
        transferNumber = editTransferNumber;

        // Remove old server-side items that are no longer in localItems
        const existingTransfer = await hteTransferService.getTransfer(editTransferNumber);
        const currentServerIds = new Set(localItems.filter(i => i.serverId).map(i => i.serverId));
        for (const oldDetail of existingTransfer.details || []) {
          if (oldDetail.id && !currentServerIds.has(oldDetail.id)) {
            await hteTransferService.removeDetail(transferNumber, oldDetail.id);
          }
        }

        // Update existing items and add new ones
        for (const item of localItems) {
          if (item.serverId) {
            await hteTransferService.updateDetail(transferNumber, item.serverId, {
              quantity_request: item.quantity_request,
              quantity_shipped: item.quantity_shipped,
              weight_shipped: item.weight_shipped,
              is_frozen: item.is_frozen,
              date_code: item.date_code || undefined,
              remark: item.remark || undefined,
            });
          } else if (draftSourceRef.current !== 'auto-generated') {
            // Only add details from frontend for manual drafts.
            // Auto-generated drafts skip this — the backend submit materializer
            // creates details with correct case counts.
            const { data: detail } = await hteTransferService.addDetail(transferNumber, {
              item_number: item.item_number,
              quantity_request: item.quantity_request,
              quantity_shipped: item.quantity_shipped,
              weight_shipped: item.weight_shipped,
              is_frozen: item.is_frozen,
              date_code: item.date_code || undefined,
              remark: item.remark || undefined,
            });
            // Add weights for new items
            for (const w of item.weights) {
              await hteTransferService.addWeight(detail.id, w.weight);
            }
          }
        }
      } else {
        // ---- Create mode: new transfer ----
        const created = await hteTransferService.createTransfer(headerData);
        transferNumber = created.transfer_number;

        for (const item of localItems) {
          const { data: detail } = await hteTransferService.addDetail(transferNumber, {
            item_number: item.item_number,
            quantity_request: item.quantity_request,
            quantity_shipped: item.quantity_shipped,
            weight_shipped: item.weight_shipped,
            is_frozen: item.is_frozen,
            date_code: item.date_code || undefined,
            remark: item.remark || undefined,
          });
          for (const w of item.weights) {
            await hteTransferService.addWeight(detail.id, w.weight);
          }
        }
      }

      // Optionally submit
      if (andSubmit) {
        await hteTransferService.submitTransfer(transferNumber, user?.username);
      }

      toast.success(editTransferNumber ? t('warehouseTransfer.updateSuccess') : t('warehouseTransfer.createSuccess'));
      onCreated(transferNumber);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save transfer');
    } finally {
      setSaving(false);
    }
  };

  // ---- Input styling helper (consistent with project) ----
  const inputCls =
    'w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500';

  if (loadingDraft) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-gray-500 dark:text-gray-400">{t('common.loading')}...</div>
      </div>
    );
  }

  return (
    <div className="space-y-5 overflow-x-hidden">
      {/* Top bar: Back + Save buttons */}
      <div className="flex flex-wrap gap-y-2 items-center justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('warehouseTransfer.backToList')}
        </button>
        {/* Save as Draft & Save & Submit — only managers and admins; supervisors cannot save/submit draft transfers */}
        {isElevatedRole(user?.role) && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? t('warehouseTransfer.savingTransfer') : t('warehouseTransfer.saveAsDraft')}
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-md shadow-sm transition-colors"
            >
              <Send className="w-4 h-4" />
              {saving ? t('warehouseTransfer.savingTransfer') : t('warehouseTransfer.saveAndSubmit')}
            </button>
          </div>
        )}
      </div>

      {/* Header section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Title bar with draft badge */}
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                {editTransferNumber
                  ? `${t('warehouseTransfer.transferNumber')} ${editTransferNumber}`
                  : t('warehouseTransfer.newTransfer')}
              </h2>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                {t('warehouseTransfer.draft')}
              </span>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('warehouseTransfer.operator')}:
              <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">
                {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.username || '-'}
              </span>
            </div>
          </div>
        </div>

        {/* Form fields grid — matches ICS detail view layout */}
        <div className="px-5 py-4">
          <div className="grid grid-cols-[100px_1fr] md:grid-cols-[130px_1fr_130px_1fr] items-center gap-x-3 gap-y-2.5">
            {/* Row 1: Request Date + Requested By */}
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.requestDate')}
            </span>
            <DatePickerDropdown
              selected={requestDate || null}
              onSelect={(date) => setRequestDate(date)}
            >
              {({ onClick }) => (
                <button type="button" onClick={onClick} className={inputCls + ' flex items-center justify-between'}>
                  {requestDate ? (() => { const d = new Date(requestDate + 'T00:00:00'); return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }); })() : 'Select date...'}
                  <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                </button>
              )}
            </DatePickerDropdown>
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.requestBy')}
            </span>
            <input
              type="text"
              value={requestBy}
              onChange={(e) => setRequestBy(e.target.value)}
              className={inputCls}
            />

            {/* Row 2: Transfer Date + Received By */}
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.transferDate')}
            </span>
            <DatePickerDropdown
              selected={transferDate || null}
              onSelect={(date) => setTransferDate(date)}
            >
              {({ onClick }) => (
                <button type="button" onClick={onClick} className={inputCls + ' flex items-center justify-between'}>
                  {transferDate ? (() => { const d = new Date(transferDate + 'T00:00:00'); return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }); })() : 'Select date...'}
                  <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                </button>
              )}
            </DatePickerDropdown>
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.receiveBy')}
            </span>
            <input
              type="text"
              value={receiveBy}
              onChange={(e) => setReceiveBy(e.target.value)}
              className={inputCls}
            />

            {/* Row 3: Memo (full width) */}
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.memo')}
            </span>
            <div className="md:col-span-3">
              <textarea
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                rows={2}
                className={inputCls}
              />
            </div>

            {/* Divider */}
            <div className="col-span-2 md:col-span-4 border-t border-gray-100 dark:border-gray-700 my-0.5" />

            {/* Row 4: Warehouse From + Location From (mutually exclusive) */}
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.warehouseFrom')}
            </span>
            <select
              value={warehouseFrom}
              onChange={(e) => { setWarehouseFrom(e.target.value); setLocationFrom(''); }}
              className={inputCls}
            >
              <option value="">-- COMPANY STORAGE --</option>
              {warehouseCodes.map(wh => (
                <option key={wh.warehouse_code} value={wh.warehouse_code}>
                  {wh.warehouse_code}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.locationFrom')}
            </span>
            <select
              value={locationFrom}
              onChange={(e) => { setLocationFrom(e.target.value); setWarehouseFrom(''); }}
              className={inputCls}
            >
              <option value="">-- N/A --</option>
              {locationCodes.map(loc => (
                <option key={loc.location_code} value={loc.location_code}>{loc.location_code}</option>
              ))}
            </select>

            {/* Row 5: Warehouse To + Location To (mutually exclusive) */}
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.warehouseTo')}
            </span>
            <select
              value={warehouseTo}
              onChange={(e) => { setWarehouseTo(e.target.value); setLocationTo(''); }}
              className={inputCls}
            >
              <option value="">-- COMPANY STORAGE --</option>
              {warehouseCodes.map(wh => (
                <option key={wh.warehouse_code} value={wh.warehouse_code}>
                  {wh.warehouse_code}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.locationTo')}
            </span>
            <select
              value={locationTo}
              onChange={(e) => { setLocationTo(e.target.value); setWarehouseTo(''); }}
              className={inputCls}
            >
              <option value="">-- N/A --</option>
              {locationCodes.map(loc => (
                <option key={loc.location_code} value={loc.location_code}>{loc.location_code}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Transfer Detail — unified container: detail form + items table + weight (mirrors VB6 layout) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Container title bar with Add Item button */}
        <div className="flex flex-wrap gap-2 items-center justify-between px-5 py-2.5 border-b border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
            {t('warehouseTransfer.transferDetails')}
          </h3>
          <div className="flex items-center gap-2">
            {/* Item No. input + Find button */}
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                placeholder={t('warehouseTransfer.itemNumber')}
                value={itemQuery}
                onChange={(e) => setItemQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && itemQuery.trim()) setShowSearch(true); }}
                className="w-28 sm:w-36 px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                onClick={() => setShowSearch(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                <Search className="w-3.5 h-3.5" />
                {t('warehouseTransfer.findItem')}
              </button>
            </div>
            {/* Remove selected item button */}
            {selectedItemId && (
              <button
                onClick={() => handleRemoveItem(selectedItemId)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-400 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Detail form — shows pending (staged) item or selected (existing) item */}
        <TransferDetailForm
          formItem={pendingItem || selectedItem}
          updateField={pendingItem ? updatePendingField : updateSelectedField}
          isPending={!!pendingItem}
          onCommitPendingItem={handleCommitPendingItem}
          itemCount={localItems.length}
          onQuantityShippedChange={handleQuantityShippedChange}
        />

        {/* Items table + Weight sidebar — inside the same container */}
        <div className="flex flex-col md:flex-row items-stretch min-w-0">
          {/* Items table */}
          <div className="w-full md:flex-1 min-w-0">
            {/* Items table body */}
            {localItems.length === 0 ? (
              <div className="p-10 text-center text-gray-400 dark:text-gray-500">
                <Package className="mx-auto w-10 h-10 mb-2 opacity-50" />
                <p className="text-sm">{t('warehouseTransfer.noItems')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">#</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('warehouseTransfer.itemNumber')}
                      </th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                        {t('warehouseTransfer.isFrozen')}
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('warehouseTransfer.itemName')}
                      </th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('warehouseTransfer.quantityRequest')}
                      </th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('warehouseTransfer.quantityShipped')}
                      </th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                        {t('warehouseTransfer.unit')}
                      </th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('warehouseTransfer.weightShipped')}
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('warehouseTransfer.remark')}
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('warehouseTransfer.dateCode')}
                      </th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {localItems.map((item, idx) => {
                      const isExpanded = expandedRowId === item.tempId;
                      const hasWeights = item.weights.length > 0;
                      const effectiveWeight = getEffectiveWeight(item);
                      const isSelected = selectedItemId === item.tempId;

                      return (
                        <tr
                          key={item.tempId}
                          onClick={() => { setPendingItem(null); setSelectedItemId(item.tempId); }}
                          className={`
                            border-b border-gray-100 dark:border-gray-700/50 cursor-pointer
                            ${isSelected ? 'bg-blue-100 dark:bg-blue-900/40 ring-1 ring-inset ring-blue-300 dark:ring-blue-700' : isExpanded ? 'bg-blue-50 dark:bg-blue-900/20' : idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-800/50'}
                            hover:bg-blue-50/70 dark:hover:bg-blue-900/20 transition-colors
                          `}
                        >
                          {/* Row number with expand chevron */}
                          <td
                            className="px-3 py-2.5 text-center text-sm text-gray-400 dark:text-gray-500 cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); setExpandedRowId(prev => prev === item.tempId ? null : item.tempId); }}
                          >
                            <div className="flex items-center justify-center gap-1">
                              <span>{idx + 1}</span>
                              {hasWeights && (
                                isExpanded
                                  ? <ChevronDown className="w-3.5 h-3.5 text-blue-500" />
                                  : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 font-mono text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                            {item.item_number}
                          </td>
                          <td className="px-3 py-2.5 text-center text-sm">
                            {item.is_frozen === 'Y' ? <span className="text-blue-500">*</span> : ''}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100">
                            {item.item_chinese_name && (
                              <span className="text-gray-500 dark:text-gray-400">{item.item_chinese_name} </span>
                            )}
                            {item.item_english_name || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-700 dark:text-gray-300">
                            {item.quantity_request.toFixed(2)}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-700 dark:text-gray-300">
                            {item.quantity_shipped.toFixed(2)}
                          </td>
                          <td className="px-3 py-2.5 text-center text-xs text-gray-500 dark:text-gray-400">
                            {item.item_unit || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100">
                            {effectiveWeight.toFixed(2)}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 truncate max-w-[120px]">
                            {item.remark || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400">
                            {item.date_code || '-'}
                          </td>
                          <td className="px-2 py-2.5 text-center">
                            <button onClick={(e) => { e.stopPropagation(); handleRemoveItem(item.tempId); }} className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Desktop-only: Right sidebar with Total Weight + Weight Info */}
          <div className="hidden md:block shrink-0 w-44 border-l border-gray-200 dark:border-gray-700">
            {/* Total Weight box */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 border-l-4 border-l-green-500 dark:border-l-green-600">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                {t('warehouseTransfer.totalWeightLabel')}
              </div>
              <div className="text-2xl font-bold font-mono text-green-600 dark:text-green-400 text-right tabular-nums">
                {totalWeight.toFixed(2)}
              </div>
            </div>

            {/* Weight info panel for expanded row */}
            {expandedItem && (
              <div className="overflow-hidden">
                <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 font-mono">
                    {expandedItem.item_number}
                  </div>
                </div>
                {expandedItem.weights.length > 0 ? (
                  <>
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-900/50">
                          <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 w-10">#</th>
                          <th className="px-3 py-1.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400">
                            {t('warehouseTransfer.weightLb')}
                          </th>
                        </tr>
                      </thead>
                    </table>
                    <div className="max-h-[192px] overflow-y-auto">
                      <table className="min-w-full">
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
                          {expandedItem.weights.map((w, wIdx) => (
                            <tr key={w.tempId} className="bg-white dark:bg-gray-800">
                              <td className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 w-10 select-none">{wIdx + 1}</td>
                              <td className="px-3 py-1 text-xs text-right font-mono text-gray-900 dark:text-gray-100">
                                {w.weight.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 px-3 py-1.5 flex justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Total</span>
                      <span className="text-xs font-medium font-mono text-gray-900 dark:text-gray-100">
                        {expandedItem.weights.reduce((sum, w) => sum + w.weight, 0).toFixed(2)}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="px-3 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">
                    {t('warehouseTransfer.noWeights')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile-only: Weight breakdown below table */}
        {expandedItem && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2 border-b border-gray-200 dark:border-gray-600">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 font-mono">
                {expandedItem.item_number}
              </div>
            </div>
            {expandedItem.weights.length > 0 ? (
              <>
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/50">
                      <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 w-10">#</th>
                      <th className="px-3 py-1.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400">
                        {t('warehouseTransfer.weightLb')}
                      </th>
                    </tr>
                  </thead>
                </table>
                <div className="max-h-[192px] overflow-y-auto">
                  <table className="min-w-full">
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
                      {expandedItem.weights.map((w, wIdx) => (
                        <tr key={w.tempId} className="bg-white dark:bg-gray-800">
                          <td className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 w-10 select-none">{wIdx + 1}</td>
                          <td className="px-3 py-1 text-xs text-right font-mono text-gray-900 dark:text-gray-100">
                            {w.weight.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 px-3 py-1.5 flex justify-between">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Total</span>
                  <span className="text-xs font-medium font-mono text-gray-900 dark:text-gray-100">
                    {expandedItem.weights.reduce((sum, w) => sum + w.weight, 0).toFixed(2)}
                  </span>
                </div>
              </>
            ) : (
              <div className="px-3 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">
                {t('warehouseTransfer.noWeights')}
              </div>
            )}
          </div>
        )}

        {/* Mobile-only: Total Weight at the bottom */}
        <div className="md:hidden px-4 py-3 border-t border-gray-200 dark:border-gray-700 border-l-4 border-l-green-500 dark:border-l-green-600">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('warehouseTransfer.totalWeightLabel')}
            </div>
            <div className="text-2xl font-bold font-mono text-green-600 dark:text-green-400 tabular-nums">
              {totalWeight.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {/* Item Search Modal */}
      <ItemSearchModal
        isOpen={showSearch}
        onClose={() => setShowSearch(false)}
        onSelect={handleSelectItem}
        initialQuery={itemQuery}
      />
    </div>
  );
};

export default CreateTransferView;
